import React, { useEffect } from 'react';
import { Flex, Box } from '@spothero/ui';
import { SearchComponentContainer } from './components/search-component/SearchComponentContainer';
import { useFetchSearchData } from 'pages/search/hooks/use-fetch-search-data';
import { SearchMap } from 'pages/search/components/SearchMap';
import { SpotList } from 'pages/search/components/SpotList';
import { SpotDetails } from 'pages/search/components/SpotDetails';
import classNames from 'classnames';
import { useReduxSearchState } from 'pages/search/hooks/use-redux-search-state';
import { SearchPageView } from 'utils/search-page-view';
import { useSpotIdSync } from 'pages/search/hooks/use-sync-spotid';
import WithOptimizelySortByRelevance from 'pages/search-transient-event-monthly/WithOptimizelySortByRelevance';
import PriceBreakdownModal from 'common/price-breakdown-modal';
import { useSearchGtmTracking } from 'pages/search/hooks/use-search-gtm-tracking';
import { useHeaderSync } from 'pages/search/hooks/use-header-sync';
import { useHandlePastTimes } from 'pages/search/hooks/use-handle-past-times';
import SearchUtils from 'utils/search-utils';
import { useSegmentPageLanding } from 'pages/search/hooks/use-segment-page-landing';
import { FilterSelection } from './components/filters/FilterSelection';
import { EventsPopup } from './components/events/modules/desktop/EventsPopup';
import useUserVehicleDefault from 'hooks/use-user-vehicle-default';
import { useAppleMapsPartnerTracking } from 'pages/search/hooks/use-apple-maps-partner-tracking';
import { getRouteParam, paramNameOptions } from 'utils/url-utils';
import StorageUtils from '@spothero/utils/storage';
import { StorageKeys, StorageTypes } from 'common/constants/storageKeys';
import SearchMeta from 'seo/search-meta';
import { useHandleEndBeforeOrEqualToStartTimes } from './hooks/search/use-handle-end-before-start';
import { useFeatureVariationWithVariables } from 'plugins/optimizely/hooks/use-feature-variation';
import { IMMEDIATE_FEATURES, VARIABLE_KEYS, VARIATION_KEYS, } from 'utils/experiment';
import AirportAnimatedLoader from 'common/airport-animated-loader';
import { useSelector } from 'react-redux';
const OperatorParamMap = {
    COLONIAL: '290',
};
const spotDetailsMobileListStyles = {
    top: '52px', // leave space for header
    height: '100%',
};
const _ResponsiveSearch = () => {
    const { isEnabled, variationKey, variableValue, } = useFeatureVariationWithVariables(IMMEDIATE_FEATURES.AIRPORT_LOADING_SCREEN_TEST_FOR_CAVU, VARIABLE_KEYS.WAIT_TIME_SECONDS);
    const isSearchPending = useSelector((state) => state.search.isPending);
    const showNoSpotsFound = useSelector((state) => state.search.data.showNoSpotsFound);
    const showLoader = isSearchPending && !showNoSpotsFound;
    const airportLoadWaitTime = isEnabled ? variableValue : 0;
    const { loadSpots, loadMoreSpots } = useFetchSearchData(airportLoadWaitTime);
    const { parkingType, view, selectedSpotId, priceBreakdownModalSpotId, priceBreakdownModalSpotRate, } = useReduxSearchState();
    const showPriceBreakdownModal = priceBreakdownModalSpotId || priceBreakdownModalSpotRate;
    const isSpotDetailsVisibleOnMobileList = selectedSpotId && view === SearchPageView.MOBILE_LIST;
    const searchClasses = classNames('Search', `parking-type-${parkingType}`, {
        'Search-list-view': view === SearchPageView.MOBILE_LIST,
    }, {
        'Search-map-view': view === SearchPageView.MOBILE_MAP,
    });
    useSpotIdSync();
    useSearchGtmTracking();
    useSegmentPageLanding();
    useAppleMapsPartnerTracking();
    useHeaderSync();
    useHandlePastTimes();
    useHandleEndBeforeOrEqualToStartTimes();
    useUserVehicleDefault();
    // https://github.com/spothero/consumer-web/blob/b64eec953b71d05dbf432e269a2a8212e25b856e/src/js/pages/search-transient-event-monthly/WithSearch.jsx#L169
    useEffect(() => {
        SearchUtils.setPreviousSearch();
        const operator_id = getRouteParam(paramNameOptions.OPERATOR_ID);
        if (operator_id === OperatorParamMap.COLONIAL) {
            StorageUtils.set(StorageKeys.PARTNER_AFFILIATE, operator_id, StorageTypes.SESSION);
        }
    }, []);
    const showAirportAnimatedLoader = isEnabled &&
        parkingType === 'airport' &&
        showLoader &&
        variationKey ===
            VARIATION_KEYS[IMMEDIATE_FEATURES.AIRPORT_LOADING_SCREEN_TEST_FOR_CAVU]?.ANIMATED_LOADING_PAGE_WITH_WAIT_TIME;
    return (<>
            {showAirportAnimatedLoader && <AirportAnimatedLoader />}

            <Box data-testid="ResponsiveSearch" position={{ tablet: 'relative', base: 'fixed' }} // "fixed" enables to all be relative to the viewport rather than the next higher anchor/body.
     zIndex="dropdown" // added to ensure the fixed search header remains above other page content on scroll - Copilot
     top="0" left="0" right="0" bottom="0" overflow="hidden" display={{ base: 'block', tablet: 'flex' }} flexFlow={{ base: '', tablet: 'column' }} height={{
            base: '100dvh',
            desktop: 'calc(100vh - var(--chakra-sizes-desktop-search-header-height-without-filters))',
        }}>
                {/* @ts-expect-error HOC component causes type error */}
                <SearchMeta hideCanonical/>
                {view === SearchPageView.DESKTOP_LIST && <FilterSelection />}
                <Flex as="main" flexFlow={{ base: 'column', tablet: 'row' }} overflow="hidden" position="relative" height="100%" sx={{
            '@media screen and (min-width: 64rem)': {
                flexFlow: 'row',
                '& .SpotMap': {
                    height: 'auto',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    top: '0',
                    display: 'block',
                    position: 'absolute',
                },
                '& .SearchMap': {
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                },
            },
            '.SpotDetailsReservationSummary-sticky': {
                display: { tablet: 'none' },
            },
            ...(isSpotDetailsVisibleOnMobileList
                ? spotDetailsMobileListStyles
                : {
                    '& .SpotMap': {
                        height: 'auto',
                        bottom: '0',
                        left: '0',
                        right: '0',
                        top: '0',
                        display: 'block',
                    },
                    '& .SearchMap': {
                        position: 'relative',
                        height: '100%',
                    },
                }),
        }} className={searchClasses}>
                    <EventsPopup />
                    {view !== SearchPageView.DESKTOP_LIST && (
        // This is done to make a placeholder for the box this will appear in
        <Box minHeight={view === SearchPageView.MOBILE_LIST
                ? '146px'
                : '0'} marginTop={view === SearchPageView.MOBILE_LIST
                ? '52px'
                : '0'}>
                            <SearchComponentContainer />
                        </Box>)}
                    <SpotList loadMoreSpots={loadMoreSpots}/>
                    {Boolean(selectedSpotId) && <SpotDetails />}
                    <SearchMap loadSpots={parkingType === 'airport' ? undefined : loadSpots} loadMoreSpots={loadMoreSpots}/>
                    {showPriceBreakdownModal && (<PriceBreakdownModal spotId={priceBreakdownModalSpotId} rate={priceBreakdownModalSpotRate}/>)}
                </Flex>
            </Box>
        </>);
};
export const ResponsiveSearch = WithOptimizelySortByRelevance(_ResponsiveSearch);
